import { Box, Button, TextField, Typography, CircularProgress } from '@mui/material'
import React, { useState, useEffect, useRef } from 'react'
import { Logo } from '../assets/images'
import signupBackground from '../assets/images/Signup-Background.png'
import AuthService from '../services/auth.service'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AccountVerification from '../modals/AccountVerification'
import moment from 'moment-timezone';
import { Applications } from '../types/enums'
import { toast } from 'react-toastify'
import { validateEmail, generateSecurePassword } from '../utils/common.util'
import { LeadsquaredService } from '../services/leadsquared/leadsquared.service'
import { ArrowForward } from '@mui/icons-material'
import { 
    SIGNUP_FLOWS,
    logEvent,
    EVENT_TYPES,
    SIGNUP_STEPS
} from '../utils/events.util'


const SignupFreePlan = () => {
    const navigate = useNavigate();
    const [leadSquaredId, setLeadSquaredId] = useState<string>('');
    const autoRegistrationAttempted = useRef(false);


    const getTimezoneString = () => {
        const timezone = moment.tz.guess() || "UTC";
        const offset = moment.tz(timezone).format("Z");
        const zoneName = moment.tz(timezone).zoneName();
        return `(GMT${offset}),${timezone},${zoneName}`;
    };

    const generateFirstName = (email: string) => {
        const emailParts = email.split("@")[0];
        const name = emailParts
            .split(/[._-]/)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(" ");
        return name;
    };

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        timezone: getTimezoneString(),
        subdomain: ''
    });
    const [formErrors, setFormErrors] = useState({
        email: '',
        subdomain: ''
    });

    // Auto-generate strong password on component mount
    useEffect(() => {
        const autoGeneratedPassword = generateSecurePassword();
        setFormData(prev => ({
            ...prev,
            password: autoGeneratedPassword
        }));
    }, []);

    const [isLoading, setIsLoading] = useState(false);

    const [verificationData, setVerificationData] = useState({
        companyId: '',
        companyName: '',
        infLeadSquaredId: ''
    });

    const [searchParams, setSearchParams] = useSearchParams();

    // Set fixed plan details - All-In-One-USD-Yearly with 10 units
    const planId = 'free-trial-plan';
    const cleanPlanId = 'free-trial-plan';



    // Store planId in localStorage when component mounts
    useEffect(() => {
        if (planId) {
            localStorage.setItem('planId', planId);
        }
    }, [planId]);


    const validateForm = () => {
        const errors = {
            email: '',
            subdomain: ''
        };
        let isValid = true;

        // Check required fields
        const emailValidation = validateEmail(formData.email);
        if (!emailValidation.isValid) {
            errors.email = emailValidation.error || "Please enter a valid email";
            isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // For subdomain field, convert to lowercase
        const processedValue = name === 'subdomain' ? value.toLowerCase() : value;

        // Update the form data with the raw input value
        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        // Clear error when user starts typing
        setFormErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };


    const [verificationDialogOpen, setVerificationDialogOpen] = React.useState(false);

    const handleVerificationDialogOpen = () => {
        setVerificationDialogOpen(true);
    };

    const handleVerificationDialogClose = async () => {
        setVerificationDialogOpen(false);
        const leadData = {
            "leadId": leadSquaredId,
            "fields": [
                { "Attribute": "mx_Plan_Name", "Value": cleanPlanId },
                { "Attribute": "mx_Status", "Value": "Pending Verification" }
            ]
        }
        await LeadsquaredService.updateLsField(leadData);
        navigate('/login');
    };

    // Helper function to clear UTM params from localStorage
    const clearUtmParamsFromStorage = () => {
        localStorage.removeItem('utm_source');
        localStorage.removeItem('utm_medium');
        localStorage.removeItem('utm_campaign');
        localStorage.removeItem('utm_content');
        localStorage.removeItem('utm_term');
    };

    const handleVerificationDialogProceed = async () => {
        // handleVerificationDialogClose();
        setVerificationDialogOpen(false);
        try {
            await LeadsquaredService.joinActivity({ id: leadSquaredId, activityEvent: 205, activityValue: "Email Verified" });
            const leadData = {
                "leadId": leadSquaredId,
                "fields": [
                    { "Attribute": "mx_Plan_Name", "Value": cleanPlanId },
                    { "Attribute": "mx_Status", "Value": "Active" }
                ]
            }
            await LeadsquaredService.updateLsField(leadData);
        } catch (error) {
            console.error('Error updating lead data:', error);
        }

        // Clear UTM parameters from localStorage after successful signup
        clearUtmParamsFromStorage();
  
        // Pass plan data through URL parameters
        const planData = {
            price: 0,
            currency: 'USD',
            plan_name: 'Free Trial Plan',
            customer_type: "free_plan"
        };

        // Encode plan data as URL parameters
        const planDataParams = new URLSearchParams({
            ...planData,
            price: planData.price.toString()
        }).toString();

        window.location.href = `/loading-dashboard?ew_email=${formData.email}&planId=${planId}&${planDataParams}`;
    };


    // Removed handleChange function as we no longer use tabs


    const handleNext = async () => {
        try {
            setIsLoading(true);

            // Validation and display errors
            const isValid = validateForm();
            if (!isValid) {
                return;
            }

            // Check if email exists
            const emailCheckResponse = await AuthService.checkEmailExists(formData.email);
            if (emailCheckResponse.data?.exists) {
                toast.error("An account with this email already exists. Please use a different email or try logging in.");
                setIsLoading(false);
                return;
            }

            // Auto-generate firstName and lastName from email
            const firstName = generateFirstName(formData.email);
            const lastName = "";

            // Generate subdomain from first name
            const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, '');
            const subdomainResponse = await AuthService.generateSubdomain(baseSubdomain, Applications.EASYWEBINAR);

            // Store the generated subdomain for use in step 2
            setFormData(prev => ({
                ...prev,
                subdomain: subdomainResponse.subdomain
            }));

            // Log signup event before moving to next step
            try {
                const eventData = {
                    step: SIGNUP_STEPS.FORM,
                    password: formData.password,
                    planId: cleanPlanId || "",
                    signupFlow: SIGNUP_FLOWS.HOMEPAGE,
                    cardProvided: false
                }
                
                await logEvent(formData.email, EVENT_TYPES.SIGNUP, eventData);
            } catch (error: any) {
                console.error('Error logging signup event:', error);
                // Don't block the flow if event logging fails
            }

            let LsId = '';

            // Complete signup in first step - no second step needed
            try {
                let utm_Data = {
                    utm_source: localStorage.getItem('utm_source'),
                    utm_medium: localStorage.getItem('utm_medium'),
                    utm_campaign: localStorage.getItem('utm_campaign'),
                    utm_content: localStorage.getItem('utm_content'),
                    utm_term: localStorage.getItem('utm_term')
                }

                // Create form data with generated firstName and lastName
                const formDataWithDetails = {
                    firstName,
                    lastName,
                    email: formData.email,
                    password: formData.password,
                    timezone: getTimezoneString(),
                    subdomain: subdomainResponse.subdomain,
                };

                LsId = await LeadsquaredService.createtLead(formDataWithDetails, "", utm_Data);
                await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 201, activityValue: "Signup Attempt - pricing" });
                const leadData = {
                    "leadId": LsId,
                    "fields": [
                        { "Attribute": "mx_Source_lp", "Value": searchParams.get('Source_IP'), },
                    ]
                }
                await LeadsquaredService.updateLsField(leadData);
                setLeadSquaredId(LsId);
            } catch (error: any) {
                console.error('Error creating lead:', error);
            }

            // Complete the signup process
            try {
                const signupData = {
                    ...formData,
                    firstName,
                    lastName,
                    phoneNumber: "",
                    timezone: getTimezoneString(),
                    planId: cleanPlanId,
                };

                const response = await AuthService.signup(signupData);
                setVerificationData({
                    companyId: response.data.companyId || '',
                    companyName: response.data.companyName || '',
                    infLeadSquaredId: response.data.inf_lead_squared_id || ''
                });
                handleVerificationDialogOpen();
                toast.success('Signup successful! Please verify your account.');
                
                // Log successful signup
                try {
                    await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 205, activityValue: "Waiting To Confirm Email Validation" });
                    await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 205, activityValue: cleanPlanId });
                    await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 205, activityValue: "Currently In Trial" });
                } catch (error: any) {
                    console.error('Error joining activities:', error);
                }
            } catch (error: any) {
                console.error('Signup failed:', error);
                toast.error(error.message || 'An error occurred during signup. Please try again.');
            }
        } catch (error: any) {
            console.error('Error generating subdomain:', error);
            toast.error('Failed to generate subdomain. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };


    function captureUTMParams() {
        const urlParams = new URLSearchParams(window.location.search);

        const utmSource = urlParams.get('utm_source');
        const utmMedium = urlParams.get('utm_medium');
        const utmCampaign = urlParams.get('utm_campaign');
        const utmContent = urlParams.get('utm_content');
        const utmTerm = urlParams.get('utm_term');
        const email = urlParams.get('email');

        if (utmSource) localStorage.setItem('utm_source', utmSource);
        if (utmMedium) localStorage.setItem('utm_medium', utmMedium);
        if (utmCampaign) localStorage.setItem('utm_campaign', utmCampaign);
        if (utmContent) localStorage.setItem('utm_content', utmContent);
        if (utmTerm) localStorage.setItem('utm_term', utmTerm);
        
        // Set email from URL parameter if present
        if (email) {
            setFormData(prev => ({
                ...prev,
                email: email
            }));
        }
    }

    // Add useEffect for capturing UTM parameters
    useEffect(() => {
        captureUTMParams();
    }, []);

    // Auto-registration when email is present in URL
    useEffect(() => {
        const handleAutoRegistration = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const email = urlParams.get('email');

            // Only proceed if email is valid, password is already generated, and we haven't attempted yet
            if (email && validateEmail(email).isValid && formData.password && !autoRegistrationAttempted.current) {
                autoRegistrationAttempted.current = true;
                try {
                    setIsLoading(true);
                    
                    // Set email in form data
                    setFormData(prev => ({
                        ...prev,
                        email: email
                    }));

                    // Wait a bit for state to update
                    await new Promise(resolve => setTimeout(resolve, 100));

                    // Check if email exists
                    const emailCheckResponse = await AuthService.checkEmailExists(email);
                    if (emailCheckResponse.data?.exists) {
                        toast.error("An account with this email already exists. Please try logging in.");
                        setIsLoading(false);
                        return;
                    }

                    // Auto-generate firstName and lastName from email
                    const firstName = generateFirstName(email);
                    const lastName = "";

                    // Generate subdomain from first name
                    const baseSubdomain = firstName.toLowerCase().trim().replace(/\s+/g, '');
                    const subdomainResponse = await AuthService.generateSubdomain(baseSubdomain, Applications.EASYWEBINAR);

                    // Store the generated subdomain
                    setFormData(prev => ({
                        ...prev,
                        subdomain: subdomainResponse.subdomain
                    }));

                    // Log signup event
                    try {
                        const eventData = {
                            step: SIGNUP_STEPS.FORM,
                            password: formData.password,
                            planId: cleanPlanId || "",
                            signupFlow: SIGNUP_FLOWS.HOMEPAGE,
                            cardProvided: false
                        }
                        
                        await logEvent(email, EVENT_TYPES.SIGNUP, eventData);
                    } catch (error: any) {
                        console.error('Error logging signup event:', error);
                    }

                    // Create lead in LeadSquared
                    try {
                        let utm_Data = {
                            utm_source: localStorage.getItem('utm_source'),
                            utm_medium: localStorage.getItem('utm_medium'),
                            utm_campaign: localStorage.getItem('utm_campaign'),
                            utm_content: localStorage.getItem('utm_content'),
                            utm_term: localStorage.getItem('utm_term')
                        }

                        const formDataWithDetails = {
                            firstName,
                            lastName,
                            email: email,
                            password: formData.password,
                            timezone: getTimezoneString(),
                            subdomain: subdomainResponse.subdomain,
                        };

                        const LsId = await LeadsquaredService.createtLead(formDataWithDetails, "", utm_Data);
                        const leadData = {
                            "leadId": LsId,
                            "fields": [
                                { "Attribute": "mx_Source_lp", "Value": searchParams.get('Source_IP'), },
                            ]
                        }
                        await LeadsquaredService.updateLsField(leadData);
                        setLeadSquaredId(LsId);

                        // Complete the signup process
                        const signupData = {
                            email: email,
                            password: formData.password,
                            timezone: getTimezoneString(),
                            subdomain: subdomainResponse.subdomain,
                            firstName,
                            lastName,
                            phoneNumber: "",
                            planId: cleanPlanId,
                        };

                        const response = await AuthService.signup(signupData);
                        setVerificationData({
                            companyId: response.data.companyId || '',
                            companyName: response.data.companyName || '',
                            infLeadSquaredId: response.data.inf_lead_squared_id || ''
                        });
                        handleVerificationDialogOpen();
                        toast.success('Registration successful! Please verify your account.');
                        
                        // Log successful signup
                        try {
                            await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 205, activityValue: "Waiting To Confirm Email Validation" });
                            await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 205, activityValue: cleanPlanId });
                            await LeadsquaredService.joinActivity({ id: LsId, activityEvent: 205, activityValue: "Currently In Trial" });
                        } catch (error: any) {
                            console.error('Error joining activities:', error);
                        }
                    } catch (error: any) {
                        console.error('Error creating lead:', error);
                        // Continue with signup even if lead creation fails
                        try {
                            const signupData = {
                                email: email,
                                password: formData.password,
                                timezone: getTimezoneString(),
                                subdomain: subdomainResponse.subdomain,
                                firstName,
                                lastName,
                                phoneNumber: "",
                                planId: cleanPlanId,
                            };

                            const response = await AuthService.signup(signupData);
                            setVerificationData({
                                companyId: response.data.companyId || '',
                                companyName: response.data.companyName || '',
                                infLeadSquaredId: response.data.inf_lead_squared_id || ''
                            });
                            handleVerificationDialogOpen();
                            toast.success('Registration successful! Please verify your account.');
                        } catch (signupError: any) {
                            throw signupError;
                        }
                    }
                } catch (error: any) {
                    console.error('Registration failed:', error);
                    toast.error(error.message || 'Registration failed. Please try manual signup.');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        // Small delay to ensure component is fully mounted
        const timer = setTimeout(() => {
            handleAutoRegistration();
        }, 100);

        return () => clearTimeout(timer);
    }, [formData.password, cleanPlanId, searchParams]);


    return (
        <>
            <Box
                sx={{
                    minHeight: "100vh",
                    width: "100vw",
                    position: "relative",
                    backgroundImage: `url(${signupBackground})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    color: "#1f2937",
                    fontFamily: "Inter",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backdropFilter: "blur(5px)",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        zIndex: 0,
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: { xs: "16px 24px", sm: "24px 32px" },
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <img
                            src={Logo}
                            alt="easywebinar logo"
                            onClick={() => window.open("https://easywebinar.com/", "_blank")}
                            style={{ height: 32, cursor: "pointer", maxWidth: "100%" }}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        endIcon={<ArrowForward />}
                        size="small"
                        onClick={() => navigate("/login")}
                        sx={{
                            bgcolor: "#6066E9",
                            color: "#fff",
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            "&:hover": { bgcolor: "#4B4EB5" },
                        }}
                    >
                        Login
                    </Button>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: {
                            xs: "calc(100vh - 120px)",
                            sm: "calc(100vh - 100px)",
                            md: "calc(100vh - 80px)",
                        },
                        p: { xs: 2, sm: 3 },
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    {/* Centered Modal */}
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: { xs: "100%", sm: "520px", md: "580px" },
                            bgcolor: "#ffffff",
                            borderRadius: "12px",
                            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                            p: { xs: 3, sm: 5, md: 6 },
                        }}
                    >
                        {/* Title */}
                        <Typography
                            variant="h4"
                            sx={{
                                color: "#000000",
                                fontSize: { xs: "24px", sm: "28px" },
                                fontWeight: 700,
                                textAlign: "center",
                                mb: isLoading ? 3 : 1,
                            }}
                        >
                            {isLoading ? "You're almost there!" : "Create your free account"}
                        </Typography>

                        {/* Loading State - Circular Progress and Status */}
                        {isLoading && (
                            <Box
                                        sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    mb: 3,
                                }}
                            >
                                <CircularProgress
                                    size={60}
                                                sx={{
                                        color: "#0382F7",
                                        mb: 2,
                                    }}
                                />
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                        color: "#666666",
                                                        fontSize: "16px",
                                        textAlign: "center",
                                                    }}
                                                >
                                    Setting up your account...
                                                </Typography>
                            </Box>
                        )}

                        {/* Subtitle - Only show when not loading */}
                        {!isLoading && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                    color: "#666666",
                                                        fontSize: "14px",
                                    textAlign: "center",
                                    mb: 4,
                                                    }}
                                                >
                                No credit card required
                                                </Typography>
                        )}

                        {/* Form */}
                            <Box sx={{ width: '100%' }}>
                                            <TextField
                                                variant="outlined"
                                                label="Work email"
                                                name="email"
                                                placeholder="you@company.com"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                error={!!formErrors.email}
                                                helperText={formErrors.email}
                                disabled={isLoading}
                                fullWidth
                                                sx={{
                                    mb: 3,
                                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: '8px',
                                        borderColor: '#D1D5DB',
                                        "&:hover": {
                                            borderColor: '#9CA3AF',
                                        },
                                    },
                                    "& .MuiOutlinedInput-root.Mui-focused": {
                                        borderColor: '#0382F7',
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#0382F7",
                                    },
                                                    "& .MuiOutlinedInput-input": {
                                                        padding: '14px 16px',
                                    },
                                }}
                            />

                                    <Button
                                        variant="contained"
                                        disableElevation
                                        disabled={isLoading}
                                fullWidth
                                onClick={handleNext}
                                        sx={{
                                    height: "48px",
                                            fontSize: "16px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    bgcolor: isLoading ? "#E5E7EB !important" : "#0382F7 !important",
                                    color: isLoading ? "#9CA3AF !important" : "#FFFFFF !important",
                                    "&:hover": { 
                                        bgcolor: isLoading ? "#E5E7EB !important" : "#006dd1 !important" 
                                    },
                                            "&.Mui-disabled": {
                                        bgcolor: isLoading ? "#E5E7EB !important" : "#9CA3AF !important",
                                        color: isLoading ? "#9CA3AF !important" : "#FFFFFF !important",
                                        opacity: 1
                                    },
                                    mb: 3,
                                }}
                            >
                                Get Started
                            </Button>

                            {/* Footer Text */}
                            <Box sx={{ textAlign: 'center' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#374151',
                                                fontSize: '14px',
                                                fontWeight: 400,
                                                lineHeight: 1.5,
                                        mb: 2,
                                            }}
                                        >
                                            100% Free Trial • Cancel Anytime • No Hidden Fees
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                        color: '#6B7280',
                                        fontSize: '12px',
                                                fontWeight: 400,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                    By starting your trial, you agree to our <a href="https://easywebinar.com/terms-conditions/" target="_blank" rel="noopener noreferrer" style={{ color: "#0382F7", textDecoration: "underline" }}>Terms of Service</a> and <a href="https://easywebinar.com/privacy/" target="_blank" rel="noopener noreferrer" style={{ color: "#0382F7", textDecoration: "underline" }}>Privacy Policy</a>
                                        </Typography>
                                    </Box>
                            </Box>
                    </Box>
                </Box>
            </Box>
            <AccountVerification
                onClose={handleVerificationDialogClose}
                open={verificationDialogOpen}
                onProceed={handleVerificationDialogProceed}
                phoneNumber=""
                email={formData.email}
                password={formData.password}
                companyId={verificationData.companyId}
                companyName={verificationData.companyName}
                infLeadSquaredId={verificationData.infLeadSquaredId}
            />
        </>
    )
}
export default SignupFreePlan
